import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useAppSelector } from '@/store';
import { WebConfirmModal, ModalThemeColors } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { DesktopSectionHeader } from '@/components/residents/desktop/myAccount/DesktopSectionHeader';
import { useConfirmModal } from '@/hooks/common/useConfirmModal';
import { useSendVerificationEmailMutation } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { strings, formatString } from '@/constants/residents/strings';
import { useThemedStyles, useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';

function useStyles() {
  return useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      root: { flex: 1, backgroundColor: colors.bg.base },
      header: { borderBottomWidth: 1, borderBottomColor: colors.text.subtle, paddingHorizontal: 20, paddingVertical: 14 },
      headerTitle: { color: colors.text.primary, fontWeight: '700', fontSize: 18 },
      body: { padding: 24 },
      copy: { color: colors.text.primary, marginBottom: 16 },
      btn: { marginTop: 12, backgroundColor: semantic.interactive.primary, paddingVertical: 12, borderRadius: 10, alignItems: 'center' },
      btnText: { color: semantic.text.inverse, fontWeight: '700' },
    }),
  );
}

function useModalThemeColors(): ModalThemeColors {
  const { colors, semantic } = useThemeColors();
  return {
    overlay: 'rgba(0,0,0,0.5)',
    modalBg: colors.card.bg,
    modalBorder: colors.card.border,
    titleText: colors.text.primary,
    messageText: colors.text.secondary,
    cancelBg: colors.bg.base,
    cancelBorder: colors.card.border,
    cancelText: colors.text.primary,
    confirmBg: semantic.interactive.danger,
    confirmText: semantic.text.inverse,
  };
}

export const ResetPasswordView: React.FC = () => {
  const styles = useStyles();
  const modalColors = useModalThemeColors();
  const user = useAppSelector((s) => (s as any).auth?.user);
  const email = user?.email || '';
  const [sendVerificationEmail, { isLoading }] = useSendVerificationEmailMutation();
  const { visible: modalVisible, open: openModal, close: closeModal } = useConfirmModal(false);
  const { showSuccess, showError } = useToast();

  const handleReset = async () => {
    try {
      // Mirror web behavior: pass targetPath to route to success page
      await sendVerificationEmail({ email, targetPath: '/account/account-reset-password/success' }).unwrap();
      openModal();
      showSuccess('Verification email sent');
    } catch (_e) {
      // no-op; could add toast
      showError('Failed to send verification email');
    }
  };

  return (
    <View style={styles.root}>
      <DesktopSectionHeader
        title={strings.account.resetPasswordTitle}
        containerStyle={styles.header}
        titleStyle={styles.headerTitle}
      />
      <View style={styles.body}>
        <Text style={styles.copy}>{strings.account.resetPasswordCopy}</Text>
        <Hoverable hover="button" style={styles.btn} onPress={handleReset} disabled={isLoading}>
          <Text style={styles.btnText}>{isLoading ? 'Sending...' : strings.account.resetPasswordButton}</Text>
        </Hoverable>
      </View>

      <WebConfirmModal
        visible={modalVisible}
        title={strings.account.resetPasswordModalTitle}
        message={formatString(strings.account.resetPasswordModalMessagePrefix, email)}
        confirmText={strings.account.resetPasswordModalResend}
        cancelText={strings.account.resetPasswordModalClose}
        onConfirm={handleReset}
        onCancel={closeModal}
        themeColors={modalColors}
      />
    </View>
  );
};

export default ResetPasswordView;
